import { useEffect, useRef, useState } from 'react';

/** Share one commit boundary and pending guard between outside clicks and Save.
 * Include the editor AND its Save/Cancel actions in boundaryRef. The caller owns
 * validation and cancellation; a rejected save stays open for correction/retry. */
export default function useInlineEditCommit({ editing, onCommit, onClose }) {
  const boundaryRef = useRef(null);
  const committing = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const commit = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!editing || committing.current) return;
    committing.current = true;
    setPending(true);
    setError(null);
    try {
      await onCommit();
      onClose();
    } catch (saveError) {
      setError(saveError);
    } finally {
      committing.current = false;
      setPending(false);
    }
  };

  useEffect(() => {
    if (!editing) {
      setError(null);
      return undefined;
    }
    const handleOutside = (event) => {
      if (boundaryRef.current && !boundaryRef.current.contains(event.target)) {
        // Select menus are portaled outside the editor's DOM boundary. Their
        // triggers identify the owned panels through aria-controls.
        const insideControlledPanel = Array.from(
          boundaryRef.current.querySelectorAll('[aria-controls]')
        ).some(control => control.getAttribute('aria-controls').split(/\s+/).some(id =>
          document.getElementById(id)?.contains(event.target)
        ));
        if (insideControlledPanel) return;
        // Do not cancel the outside control's own mouse interaction.
        commit();
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  });

  return { boundaryRef, commit, pending, error };
}
